import { useCallback, useRef, useState } from 'react';
import { Check } from 'react-feather';
import styled from '@emotion/styled';
import { ClickAwayListener } from '@mui/material';
import { useFloating } from '@floating-ui/react';
import { PopperMenu, PopperMenuItem } from '../Popper/PopperMenu';
import { Portal } from '../Portal/index.js';
import { MoreHorizontalAnchorSmall } from '../presentation/Anchor';
import { useZIndexMiddleware } from '../presentation/popper-modifiers';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import type { ProjectClips } from '@trinity/chat/types';
import styles from './style/Cells.Actions.module.css';

type Props = {
  monologues: ProjectClips.Monologue[];
  range: { start: number; end: number };
  transcriptId: number;
  /**
   * If provided, a "Download Clip" menu item is shown.
   * The chat app omits this prop since it does not support clip downloads.
   */
  onDownloadClip?: () => void;
};

export const ActionsMenu = (props: Props) => {
  const [open, setOpen] = useState(false);

  const zIndexMiddleware = useZIndexMiddleware(11);
  const { refs, floatingStyles } = useFloating({
    middleware: [zIndexMiddleware],
    placement: 'bottom-start',
  });

  const handleBlur = useCallback(() => setOpen(false), []);
  const handleClick = useCallback(() => setOpen(true), []);

  const handleDownloadClip = useCallback(() => {
    props.onDownloadClip?.();
    setOpen(false);
  }, [props.onDownloadClip]);

  const { showCopied, copy } = useCopy();
  const handleCopy = useCallback(async () => {
    const content = props.monologues
      .map(({ speaker, text }) => `${speaker.name || `Speaker ${speaker.index}`}:\n${text}`)
      .join('\n\n');
    await copy(content);
  }, [copy, props.monologues]);

  return (
    <>
      <div ref={refs.setReference} onClick={handleClick}>
        <MoreHorizontalAnchorSmall open={open} />
      </div>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleBlur} touchEvent="onTouchStart">
            <div ref={refs.setFloating} style={floatingStyles}>
              <PopperMenu>
                {props.onDownloadClip && (
                  <PopperMenuItem onClick={handleDownloadClip}>Download Clip</PopperMenuItem>
                )}
                <PopperMenuItem onClick={handleCopy}>
                  <CopyWrap>
                    {showCopied && <Check size={14} />}
                    {showCopied ? 'Copied!' : 'Copy Text'}
                  </CopyWrap>
                </PopperMenuItem>
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  );
};

ActionsMenu.displayName = 'ProjectClips.ActionsMenu';

const useCopy = () => {
  const [showCopied, setShowCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const copyToClipboard = useCopyToClipboard();

  const handleCopy = useCallback(
    async (value: string) => {
      setShowCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setShowCopied(false), 2000);
      await copyToClipboard(value);
    },
    [copyToClipboard]
  );

  return { showCopied, copy: handleCopy };
};

const CopyWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
